import { Chip, Stack, Tooltip } from '@mui/material';
import type { MachineSummary } from '../../api/types';
import { fmtDateTime } from '../../utils/format';

// Two-Status Reporting (design §5) — a machine shows TWO independent chips:
//   * ESP32 chip — the ESP32 ↔ SC link ("device online").
//   * SAS chip   — the machine reporting SAS ("machine online").
// Both are gated first by lifecycle (decommissioned) and commissioning
// progress (no QR / no device), mirroring the Core MachineChipRules so the
// portal and the tech app read the same way. No raw enums; em-dash for null
// timestamps; local-TZ on the _at fields (per PORTAL_FORM_RULES).

type ChipState = 'decommissioned' | 'no-qr' | 'no-device' | 'online' | 'offline';

type ChipVisual = { label: string; color: 'success' | 'default' | 'warning' };

function esp32State(m: MachineSummary): ChipState {
  if (m.status === 'decommissioned') return 'decommissioned';
  if (!m.hasQrCode) return 'no-qr';
  if (!m.deviceId) return 'no-device';
  return m.esp32Online ? 'online' : 'offline';
}

function sasState(m: MachineSummary): ChipState {
  if (m.status === 'decommissioned') return 'decommissioned';
  if (!m.hasQrCode) return 'no-qr';
  if (!m.deviceId) return 'no-device';
  return m.sasOnline ? 'online' : 'offline';
}

function visual(kind: 'ESP32' | 'SAS', state: ChipState): ChipVisual {
  switch (state) {
    case 'online':         return { label: `${kind} online`,  color: 'success' };
    case 'offline':        return { label: `${kind} offline`, color: 'warning' };
    case 'no-device':      return { label: 'no device',       color: 'default' };
    case 'no-qr':          return { label: 'no QR',           color: 'default' };
    case 'decommissioned': return { label: 'decommissioned',  color: 'default' };
  }
}

function OneChip({
  kind, state, at,
}: { kind: 'ESP32' | 'SAS'; state: ChipState; at?: string | null }) {
  const v = visual(kind, state);
  // The last transition time is only meaningful for online/offline.
  const tip = state === 'online' || state === 'offline'
    ? `Last change: ${fmtDateTime(at) || '—'}`
    : undefined;
  const chip = (
    <Chip
      size="small"
      variant={state === 'online' ? 'filled' : 'outlined'}
      label={v.label}
      color={v.color}
    />
  );
  return tip ? <Tooltip title={tip}>{chip}</Tooltip> : chip;
}

/** The two status chips for a machine, side by side. */
export function MachineStatusChips({ machine }: { machine: MachineSummary }) {
  return (
    <Stack direction="row" spacing={0.5} sx={{ py: 0.5 }} alignItems="center">
      <OneChip kind="ESP32" state={esp32State(machine)} at={machine.esp32OnlineAt} />
      <OneChip kind="SAS" state={sasState(machine)} at={machine.sasOnlineAt} />
    </Stack>
  );
}
